'use client';

import { useRef, useState } from 'react';

import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

export interface CarouselTestimonial {
  quote: string;
  author?: string;
  role?: string;
  rating?: number;
}

/** Two reviews per view on wide screens, one on phones; native scroll-snap with page dots. */
export function TestimonialCarousel({ items, dark }: { items: CarouselTestimonial[]; dark: boolean }) {
  const { t } = useTranslation();
  const track = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState({ active: 0, count: Math.ceil(items.length / 2) });

  const measure = () => {
    const element = track.current;
    if (!element) return;
    const perView = Math.max(1, Math.round(element.clientWidth / (element.firstElementChild?.clientWidth || element.clientWidth)));
    const count = Math.max(1, Math.ceil(items.length / perView));
    const active = Math.min(count - 1, Math.round(Math.abs(element.scrollLeft) / element.clientWidth));
    setPage((previous) => (previous.active === active && previous.count === count ? previous : { active, count }));
  };

  const goTo = (index: number) => {
    const element = track.current;
    if (!element) return;
    const direction = getComputedStyle(element).direction === 'rtl' ? -1 : 1;
    element.scrollTo({ left: direction * index * element.clientWidth, behavior: 'smooth' });
  };

  return (
    <div>
      <div
        ref={track}
        onScroll={measure}
        onPointerEnter={measure}
        className="flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-test="landing-testimonial-carousel"
      >
        {items.map((item, index) => (
          <figure
            key={index}
            className={cn('flex w-full shrink-0 snap-start flex-col rounded-[20px] p-8 md:w-[calc(50%-0.75rem)]', dark ? 'bg-white/5' : 'bg-[var(--landing-surface)]')}
          >
            {item.rating ? (
              <div className="flex gap-1 text-amber-400" role="img" aria-label={t('marketing:landingRating', { rating: item.rating })}>
                {Array.from({ length: 5 }, (_, star) => <Star key={star} className={cn('h-5 w-5', star < item.rating! ? 'fill-current' : 'opacity-30')} aria-hidden />)}
              </div>
            ) : null}
            <blockquote className="landing-prose mt-4 flex-1 leading-relaxed">{item.quote}</blockquote>
            {item.author || item.role ? (
              <figcaption className="mt-6">
                {item.author ? <p className="font-semibold">{item.author}</p> : null}
                {item.role ? <p className="text-sm opacity-70">{item.role}</p> : null}
              </figcaption>
            ) : null}
          </figure>
        ))}
      </div>
      {page.count > 1 ? (
        <div className="mt-8 flex justify-center gap-2">
          {Array.from({ length: page.count }, (_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => goTo(index)}
              aria-label={t('marketing:landingGoToSlide', { index: index + 1 })}
              aria-current={index === page.active || undefined}
              className={cn('h-2.5 rounded-full transition-all', index === page.active ? 'w-8 bg-primary' : 'w-2.5 bg-current opacity-25')}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
